import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit, faCalendar } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles } from "../../themes/TableStyles";
import CustomDatePicker, { getDateFromISOStringIgnoringTimezone, convertToThDate } from "../CustomDatePicker";

const VaccineLotManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [vaccineLots, setVaccineLots] = useState([]);
  const [filteredVaccineLots, setFilteredVaccineLots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLot, setSelectedLot] = useState(null);
  const [formData, setFormData] = useState({
    ProductionDate: "",
    ExpiryDate: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสวัคซีน",
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "150px",
    },
    {
      name: "ชื่อวัคซีน",
      selector: (row) => row.VaccineNameTH || "-",
      sortable: true,
    },
    {
      name: "Lot No.",
      selector: (row) => row.LotID,
      sortable: true,
      width: "150px",
    },
    {
      name: "วันที่ผลิต",
      selector: (row) => row.ProductionDate ? convertToThDate(row.ProductionDate) : "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "วันที่หมดอายุ",
      selector: (row) => row.ExpiryDate ? convertToThDate(row.ExpiryDate) : "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "ผลการทดสอบคุณภาพ",
      selector: (row) => {
        if (row.QCResultPassed === null || row.QCResultPassed === undefined) {
          return "-";
        }
        return row.QCResultPassed ? "ผ่าน" : "ไม่ผ่าน";
      },
      sortable: true,
      width: "150px",
    },
    /*
    {
      name: "จำนวนที่ส่งทดสอบ (ขวด)",
      selector: (row) => row.QCQuantity ? row.QCQuantity.toLocaleString("th-TH") : "-",
      sortable: true,
      width: "180px",
    },
    {
      name: "จำนวนที่ทดสอบผ่าน (ขวด)",
      selector: (row) => row.QCAmountPassed ? row.QCAmountPassed.toLocaleString("th-TH") : "-",
      sortable: true,
      width: "180px",
    },
    {
      name: "จำนวนที่ทดสอบไม่ผ่าน (ขวด)",
      selector: (row) => row.QCAmountFailed ? row.QCAmountFailed.toLocaleString("th-TH") : "-",
      sortable: true,
      width: "200px",
    },
    */
    {
      name: "หมายเหตุ QC",
      selector: (row) => row.QCResultRemark || "-",
      sortable: false,
      wrap: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchVaccineLots = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineLotsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineLotsList || [];
        setVaccineLots(data);
        setFilteredVaccineLots(data);
      }
    } catch (error) {
      console.error("Error fetching vaccine lots:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineLots();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value);
  };

  const applyFilters = (search) => {
    let filtered = vaccineLots;

    if (search) {
      // แยกคำค้นหาด้วย comma หรือ space
      const searchTerms = search
        .split(/[,\s]+/)
        .map(term => term.trim())
        .filter(term => term.length > 0)
        .map(term => term.toLowerCase());

      filtered = filtered.filter((row) => {
        const vaccineId = (row.VaccineID || "").toLowerCase();
        const vaccineName = (row.VaccineNameTH || "").toLowerCase();
        const lotId = (row.LotID || "").toLowerCase();
        
        // รวมข้อมูลทั้งหมดเป็น string เดียวสำหรับการค้นหา
        const combinedText = `${vaccineId} ${vaccineName} ${lotId}`;
        
        // ตรวจสอบว่าทุกคำค้นหามีอยู่ใน combinedText หรือไม่
        return searchTerms.every(term => combinedText.includes(term));
      });
    }

    setFilteredVaccineLots(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredVaccineLots(vaccineLots);
  };

  const handleEdit = (row) => {
    setSelectedLot(row);
    // แปลงวันที่จาก ISO string หรือ Date object เป็น format ที่ CustomDatePicker ต้องการ
    let productionDate = "";
    let expiryDate = "";
    
    if (row.ProductionDate) {
      const prodDate = getDateFromISOStringIgnoringTimezone(row.ProductionDate);
      productionDate = prodDate ? row.ProductionDate.split('T')[0] : "";
    }
    
    if (row.ExpiryDate) {
      const expDate = getDateFromISOStringIgnoringTimezone(row.ExpiryDate);
      expiryDate = expDate ? row.ExpiryDate.split('T')[0] : "";
    }
    
    setFormData({
      ProductionDate: productionDate,
      ExpiryDate: expiryDate,
    });
    onOpen();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    if (!selectedLot) return;

    setIsSaving(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineLot",
        data: {
          LotID: selectedLot.LotID,
          VaccineID: selectedLot.VaccineID,
          ProductionDate: formData.ProductionDate || null,
          ExpiryDate: formData.ExpiryDate || null,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        alert("อัปเดตข้อมูลสำเร็จ!");
        onClose();
        fetchVaccineLots();
      } else {
        alert("เกิดข้อผิดพลาด: " + (response.error || "ไม่สามารถอัปเดตข้อมูลได้"));
      }
    } catch (error) {
      console.error("Error updating vaccine lot:", error);
      alert("เกิดข้อผิดพลาดในการอัปเดต: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedLot(null);
    setFormData({
      ProductionDate: "",
      ExpiryDate: "",
    });
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              จัดการ Lot ของวัคซีน
            </Heading>
          </HStack>
        </Box>

        {/* ส่วนตัวกรอง */}
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "400px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาจาก รหัสวัคซีน / ชื่อวัคซีน / Lot No. (คั่นด้วย comma หรือ space)"
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
        </HStack>

        {/* ส่วนลิสต์ */}
        {isLoading ? (
          <Spinner size="md" />
        ) : vaccineLots.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูล Lot ของวัคซีน
          </Text>
        ) : filteredVaccineLots.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredVaccineLots}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal สำหรับแก้ไข */}
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalOverlay />
        <ModalContent><ModalHeader
            bg="iBlue.600"
            color="white"
            borderTopRadius="md"
            >
            แก้ไข Lot ของวัคซีน - {selectedLot?.VaccineID}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Box w="100%">
                <Text fontWeight="bold" mb={2}>วัคซีน : {selectedLot?.VaccineID} - {selectedLot?.VaccineNameTH || "-"}</Text>
              </Box>
              <Box w="100%">
                <Text fontWeight="bold" mb={2}>Lot การผลิต : {selectedLot?.LotID}</Text>
              </Box>
              <FormControl>
                <FormLabel>
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: "8px" }} />
                  วันที่ผลิต
                </FormLabel>
                <CustomDatePicker
                  name="ProductionDate"
                  value={formData.ProductionDate}
                  onChange={handleInputChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: "8px" }} />
                  วันที่หมดอายุ
                </FormLabel>
                <CustomDatePicker
                  name="ExpiryDate"
                  value={formData.ExpiryDate}
                  onChange={handleInputChange}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter style={{ display: 'flex', gap: '8px' }}>
            <IconButton.Close onClick={handleClose} label="ยกเลิก" />
            <IconButton.Save
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="กำลังบันทึก..."
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VaccineLotManagement;

